import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
// @ts-expect-error importing css
import './style.css';
import SearchPage from './features/search/SearchPage';
import { AuthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ClientConfigContext } from './config/clientConfig';
import { SessionContext } from './session/sessionContext';
import { v4 as uuidv4 } from 'uuid';
import { initializeTimerRefreshListeners, isUserActive, continueSession, refreshUserActivityTimestamp } from './features/session/sessionTimeout';
import SessionTimeoutModal from './features/session/components/SessionTimeoutModal';

const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

/** Clears a stuck interaction flag left by an interrupted redirect (sessionStorage + cookie). */
function clearStuckMsalInteraction() {
    sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
    document.cookie = `${MSAL_INTERACTION_STATUS_KEY}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
}

function App() {
    const clientConfiguration = useContext(ClientConfigContext);
    const [showSessionDialog, setSessionDialogVisible] = useState(false);
    const request = useMemo(() => {
        return { scopes: clientConfiguration.b2cTenantConfig.scopes };
    }, [clientConfiguration.b2cTenantConfig.scopes]);

    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const loginAttempted = useRef(false);

    // This hook performs a login using MSAL on startup. It waits for the InteractionStatus to be inactive before running
    useEffect(() => {
        if (loginAttempted.current || isAuthenticated || inProgress !== InteractionStatus.None) {
            return;
        }
        loginAttempted.current = true;

        instance.loginRedirect(request).catch((err: { errorCode?: string }) => {
            if (err?.errorCode === 'interaction_in_progress') {
                // In certain cases, when the login is interrupted, the MSAL state is corrupted and needs to be cleared before re-login
                clearStuckMsalInteraction();
                instance.loginRedirect(request).catch((retryErr) => {
                    console.error('MSAL loginRedirect retry failed:', retryErr);
                });
                return;
            }
            console.error('MSAL loginRedirect failed:', err);
        });
    }, [instance, inProgress, isAuthenticated, request]);

    initializeSessionTimeout(clientConfiguration.userActivityLimitSeconds);
    refreshUserActivityTimestamp();

    let idlenessTimer: NodeJS.Timeout;

    function initializeSessionTimeout(userActivityLimitSeconds: number): void {
        if (!idlenessTimer) {
            initializeTimerRefreshListeners();
            idlenessTimer = setInterval(() => {
                if (!isUserActive(userActivityLimitSeconds)) {
                    setSessionDialogVisible(true);
                }
            }, 1000);
        }
    }

    return (
        <AuthenticatedTemplate>
            <SessionContext.Provider value={uuidv4()}>
                <SearchPage />
                <SessionTimeoutModal
                    open={showSessionDialog}
                    onClose={() => {
                        setSessionDialogVisible(false);
                    }}
                    onSelection={(sessionContinue: boolean) => {
                        continueSession(sessionContinue, instance, request.scopes);
                    }}
                />
            </SessionContext.Provider>
        </AuthenticatedTemplate>
    );
}

export default App;
